import { Divider, Stack } from '@mui/material';
import type { DynamicInvoiceMeasurementChartMeter } from '@vy/api-definition';
import type { AggregationOperator, Currency, Period } from '@vy/types';
import { first, isEmptyArray, isNullOrUndefined } from '@vy/utils';
import React, { FC, memo, useMemo } from 'react';
import { UsageLineItemBarChart } from '../../dynamic-invoice-view-components/UsageLineItemChart';
import { UsagePerUnitLineItemChart } from '../../dynamic-invoice-view-components/UsagePerUnitLineItemChart';
import { filterMeterDataByPeriod } from '../utils/filter-meter-data-by-period';

type UsageItemChartProps = {
  meterData: DynamicInvoiceMeasurementChartMeter;
  meterName: string;
  meterUnits: string | null;
  aggregationOperator: AggregationOperator;
  period: Period;
  currency?: Currency;
  customerId: string;
  meterNamesMap?: Record<string, string>;
  selectedPeriodRange?: { from: Date; to: Date };
};

const UsageItemChartComponent: FC<UsageItemChartProps> = ({
  meterData,
  meterName,
  meterUnits,
  aggregationOperator,
  period,
  currency,
  meterNamesMap,
  selectedPeriodRange,
}) => {
  const meterIds = Object.keys(meterData);

  if (isEmptyArray(meterIds)) {
    return null;
  }

  const filteredMeterData = useMemo(
    () => filterMeterDataByPeriod(meterData, selectedPeriodRange),
    [meterData, selectedPeriodRange],
  );

  const isMultiMeter = meterIds.length > 1;

  if (isMultiMeter) {
    const filteredMeterIds = Object.keys(filteredMeterData);

    if (isEmptyArray(filteredMeterIds)) {
      return null;
    }

    const effectiveMeterNamesMap =
      meterNamesMap ||
      filteredMeterIds.reduce(
        (acc, meterId) => {
          acc[meterId] = meterName;
          return acc;
        },
        {} as Record<string, string>,
      );

    return (
      <>
        <Divider />
        <Stack sx={{ py: 7.5, pl: 6.5, pr: 4, gap: 5 }}>
          <UsagePerUnitLineItemChart
            productMetersMeasurementData={filteredMeterData}
            aggregationOperator={aggregationOperator}
            meterName={meterName}
            meterUnits={meterUnits ?? undefined}
            currency={currency}
            period={period}
            meterNamesMap={effectiveMeterNamesMap}
          />
        </Stack>
      </>
    );
  }

  const firstMeterId = first(meterIds);

  if (isNullOrUndefined(firstMeterId)) {
    return null;
  }

  const meterUsageData = filteredMeterData[firstMeterId];

  if (isNullOrUndefined(meterUsageData) || isEmptyArray(Object.keys(meterUsageData))) {
    return null;
  }

  return (
    <>
      <Divider />
      <Stack sx={{ py: 7.5, pl: 6.5, pr: 4, gap: 5 }}>
        <UsageLineItemBarChart
          datasetLabel={meterName}
          meterUsageData={meterUsageData}
          period={period}
          aggregationOperator={aggregationOperator}
          meterName={meterName}
          meterUnits={meterUnits ?? undefined}
          currency={currency}
        />
      </Stack>
    </>
  );
};

export const UsageItemChart = memo(UsageItemChartComponent);
